import { UserAgent as SIPUserAgent, Inviter, Registerer, RegistererState, SessionState, UserAgentState } from "sip.js";
import { SIP_SERVER, REGISTRATION_EXPIRES, BASE_URL } from "./constants.js";
import {Session} from "./Session.js"
import { isTokenValid, SessionType } from "./utils.js";
import { InvalidValueException, UnknownException } from "./exceptions.js";

class UserAgent extends SIPUserAgent{
    #authObj;
    #listeners;
    #session;
    #registerer;
    #updatingAuth=false;
    #reRegisterAttempts=0;
    #reConnectAttempts=0;
    #maxRetryAttempts = 3;
    #primaryVN;

    constructor({authObj, audioElements, listeners, logLevel}){
        const options = {
            transportOptions: {
              server: `wss://${SIP_SERVER}`,
              traceSip: true,
              connectionTimeout: 15,
            },
            logLevel: logLevel || 'debug',
            autostart: false,
            register: false,
        };
        options.uri = SIPUserAgent.makeURI( `sip:${authObj.sipUsername}@${SIP_SERVER}` );
        super(options);
        this.#authObj = authObj;
        this.localAudioElement = audioElements.local;
        this.remoteAudioElement = audioElements.remote;
        this.#listeners = listeners;
        
        this.delegate = {
            onConnect: () => {
                this.#reConnectAttempts = 0;
                this.#listeners.onConnectionStateChange("UserAgentState", this.state);
                this.startRegistration();
            },

            onDisconnect: (error) => {
                this.#listeners.onConnectionStateChange("UserAgentState", this.state, this.#reConnectAttempts >= this.#maxRetryAttempts, error);
                if(this.#reConnectAttempts < this.#maxRetryAttempts) {
                    this.#reConnectAttempts++;
                    if(error) {
                        this.reConnect();
                    }
                    else {
                        this.#reConnectAttempts = this.#maxRetryAttempts;
                        this.#listeners.onConnectionStateChange("UserAgentState", this.state, this.#reConnectAttempts >= this.#maxRetryAttempts, error);
                    }
                }
            },

            onInvite: (invitation) => {
                const remoteContact = invitation.incomingInviteRequest._transaction._request.from?.displayName;
                this.#session = new Session(invitation, SessionType.Incoming, this);
                this.#listeners.onCallCreated(SessionType.Incoming, {candidate: remoteContact});
            }
        }
        this.start();
    }

    async startRegistration() {
        const options = {
            refreshFrequency: 90,
            expires: REGISTRATION_EXPIRES,
            ["extraHeaders"]: [`token: ${this.#authObj.getSipToken}`],
        }
        this.#registerer = new Registerer(this, options);
        
        this.#registerer.stateChange.addListener(async (newState) => {
            this.#listeners.onConnectionStateChange("RegisterState", newState, this.#reRegisterAttempts >= this.#maxRetryAttempts);
            switch (newState) {
                case RegistererState.Unregistered:
                    if(!this.#updatingAuth && this.#authObj.getIsLoggedIn && this.#reRegisterAttempts < this.#maxRetryAttempts) {
                        this.#reRegisterAttempts++;
                        await this.reRegister();
                    }
                    break;
                case RegistererState.Registered:
                    this.#reRegisterAttempts = 0;
                    break;
                default:
                    break;
            }
        })
        await this.#registerer.register();
    }

    async #updatePrimaryVirtualNumber(virtualNumber){
        let response = await fetch(BASE_URL+"/auth/update-userprofile/",{
            method:'PATCH',
            headers:{
                'authorization': `Secret-Key ${this.#authObj.getSecretKey}`,
                'Content-type':'application/json'
            },
            body: JSON.stringify({'primary_vn': virtualNumber})
        });
        const status = response.status;
        response = await response.json();
        if(status === 400){
            throw new InvalidValueException('Softphone.makeCall', 'virtualNumber', virtualNumber, ['A virtual number linked to your account'], {detail: JSON.stringify(response.message) || 'Invalid number'})
        }
        else if(status !== 200){
            throw new UnknownException('Softphone.makeCall', {details: JSON.stringify(response.message || '')});
        }
        if(status === 200){
            this.#primaryVN = response.data?.primary_virtual_number?.country_code + response.data?.primary_virtual_number?.number
            return true;
        }
    }

    async makeCall(candidateNumber, virtualNumber) {
        try{
            if(virtualNumber && this.#primaryVN !== virtualNumber){
                await this.#updatePrimaryVirtualNumber(virtualNumber)
            }
            const candidate = UserAgent.makeURI("sip:" + candidateNumber + "@" + SIP_SERVER);
            const inviter = new Inviter(this, candidate, {
                sessionDescriptionHandlerOptions: {
                    constraints: { audio: true, video: false },
                },
                extraHeaders: [`token: ${this.#authObj.getSipToken}`],
            });
            this.#session = new Session(inviter, SessionType.Outgoing, this);
            this.#session.remoteContact = candidateNumber;
            this.#listeners.onCallCreated(SessionType.Outgoing, {candidate: candidateNumber});
            await this.#session.getSession.invite();
            return true;
        }
        catch(err){
            if(err instanceof InvalidValueException || err instanceof UnknownException)
                throw err;
            console.error('Error in Softphone.makeCall', err);
            return false;
        }
    }

    get getSession() {
        return this.#session;
    }

    get getListeners() {
        return this.#listeners;
    }

    /**@param {Boolean} toggle  */
    set updatingAuth(toggle) {
        this.#updatingAuth = toggle;
    }

    async reConnect() {
        try {
            if(this.state !== UserAgentState.Stopped) await this.stop();
            await this.start();
        }
        catch(error) {
            // ignore invalid state transition & register attempt on 'Terminated' state errors
            console.warn(error);
        }
        if(this.#registerer.state !== RegistererState.Registered) {
            await this.reRegister();
        }
    }

    async reRegister() {
        try {
            await this.#registerer.dispose();
        }
        catch(error) {
            // ignore invalid state transition error
            console.warn(error);
        }
        this.#registerer = null;
        if(!isTokenValid(this.#authObj.getSipToken)) {
            await this.#updateToken();
        }
        await this.startRegistration();
    }

    async #updateToken() {
        this.updatingAuth = true;
        await this.#authObj.registerSoftphone();
        this.updatingAuth = false;
    }
}

export default UserAgent;